import { el } from "@dhx/ts-common/dom";
import { isDefined } from "@dhx/ts-common/core";
import { getFormItemCss, validateInput, getValidationMessage, isEmptyObj } from "../helper";
import { Label } from "./helper/label";
import { IEventSystem, EventSystem } from "@dhx/ts-common/events";
import { IInput, ValidationStatus, IBaseInputEventHandlersMap, BaseElementEvent } from "../types";

const INIT_DEBOUNCE_TIME = 500;

export class Input extends Label {
	public config: IInput;
	public events: IEventSystem<BaseElementEvent, IBaseInputEventHandlersMap> = new EventSystem();

	private _debounceTime: number = INIT_DEBOUNCE_TIME;
	private _last: number;
	private _debounceTimer: any;

	constructor(container: HTMLElement | string, config = {}) {
		super(null, config);
		this.setConfig(config);
	}
	validate(): boolean {
		const requiredCondition = !this.config.required || this.config.value;
		const isValid = !this.config.validation || validateInput(this.config.value, this.config.validation);
		this.config.$validationStatus = requiredCondition && isValid
			? ValidationStatus.success
			: ValidationStatus.error;
		this.paint();
		return requiredCondition && isValid;
	}
	clearValidate(): void {
		this.config.$validationStatus = ValidationStatus.pre;
		this.paint();
	}
	clear(): void {
		this.config.value = "";
		this.paint();
	}
	setValue(value: string): void {
		this.events.fire(BaseElementEvent.change, [value]);
		this.config.value = value;
		this.paint();
	}
	setConfig(config: IInput): void {
		if (isEmptyObj(config)) {
			return;
		}
		this.config = {
			type: this.config.type,
			id: this.config.id,
			name: this.config.name,
			value: ""
		};
		for (const key in config) {
			if (key !== "id" && key !== "type" && key !== "name") {
				this.config[key] = config[key];
			}
		}

		this.events.on(BaseElementEvent.change, value => {
			this.config.value = value || "";
		});

		this.events.fire(BaseElementEvent.configUpdate, [this.config]);
		this.paint();
	}
	getValue(): string {
		return this.config.value || "";
	}
	protected _init() {
		const { validation, value } = this.config;
		if (validation && value) {
			const isValid = validateInput(value, validation);
			this.config.$validationStatus = isValid
				? ValidationStatus.success
				: ValidationStatus.error;
		}
	}
	protected _getHandlers() {
		return {
			oninput: (e: Event) => {
				const value = (e.target as HTMLInputElement).value.trim();
				this.config.value = value;
				if (this._debounceTimer) {
					clearTimeout(this._debounceTimer);
				}
				const time = Date.now();
				const diff = this._last ? time - this._last : this._debounceTime;
				this._last = time;
				this._debounceTime = (diff + this._debounceTime) / 2 + 300;

				this._debounceTimer = setTimeout(() => {
					this._validate(value);
				}, this._debounceTime);
			},
			onblur: () => {
				this._validate(this.config.value, true);
			}
		};
	}
	protected _draw() {
		const { id,	value, disabled, name, icon, placeholder, required,	inputType, validation, hidden, autocomplete } = this.config;
		const visibility = hidden ? " dhx_form-group--hidden" : "";
		return el("div.dhx_form-group", {
			class: getFormItemCss(this.config, Boolean(required) || Boolean(validation)) + visibility
		}, [
			this._drawLabel(),
			el(".dhx_input__wrapper", {
			}, [
				el("div.dhx_input__container", {
				}, [
					this.config.icon ? el(".dhx_input__icon", {
						class: this.config.icon
					}) : null,
					el("input.dhx_input", {
						type: inputType,
						id: id || this._uid,
						placeholder: placeholder || "",
						value: isDefined(value) ? value : "",
						name: name || "",
						disabled,
						required,
						onblur: this._handlers.onblur,
						oninput: this._handlers.oninput,
						class: icon ? "dhx_input--icon-padding" : "",
						autocomplete: autocomplete ? "on" : "off"
					}),
				]),
				getValidationMessage(this.config) && el("span.dhx_input__caption", getValidationMessage(this.config))
			]),
		]);
	}
	private _validate(value: string, blur?: boolean) {
		if (blur && this._debounceTimer) {
			clearTimeout(this._debounceTimer);
		}
		this._last = null;
		this._debounceTimer = null;
		this._debounceTime = INIT_DEBOUNCE_TIME;
		if (this.config.validation) {
			if (!validateInput(value, this.config.validation)) {
				this.config.$validationStatus = ValidationStatus.error;
				this.paint();
				return;
			} else {
				this.config.$validationStatus = ValidationStatus.success;
				this.paint();
			}
		} else if (this.config.required) {
			if (value === "") {
				this.config.$validationStatus = ValidationStatus.error;
				this.paint();
			} else {
				this.config.$validationStatus = ValidationStatus.success;
				this.paint();
			}
		}
		this.events.fire(BaseElementEvent.change, [value]);
	}
}